import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { OverviewReportTopProductsItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

/** The backend's overview lists its best sellers five deep. */
export const OVERVIEW_TOP_PRODUCTS = 5;

export interface TopProductsCardProps {
  items: readonly OverviewReportTopProductsItem[] | undefined;
  isLoading: boolean;
  /** "View all" target: the products tab with the same range. */
  viewAll: ReactNode;
}

export function TopProductsCard({ items, isLoading, viewAll }: TopProductsCardProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="flex flex-row items-center justify-between border-b px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <CardTitle className="text-[15px]">{t('overview.topProducts')}</CardTitle>
          {items && items.length > 0 ? (
            <p className="text-muted-foreground text-xs">
              {t('topN', { count: OVERVIEW_TOP_PRODUCTS })}
            </p>
          ) : null}
        </div>
        {viewAll}
      </CardHeader>
      <Table aria-label={t('overview.topProducts')}>
        <TableHeader>
          <TableRow>
            <TableHead className="pl-5">{t('columns.product')}</TableHead>
            <TableHead className="text-right">{t('columns.units')}</TableHead>
            <TableHead className="pr-5 text-right">{t('columns.net')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={3} className="px-5">
                  <Skeleton className="h-5" />
                </TableCell>
              </TableRow>
            ))
          ) : items && items.length > 0 ? (
            items.map((item, i) => (
              <TableRow key={item.productId ?? `${item.title}-${i}`}>
                <TableCell className="max-w-0 truncate pl-5 font-medium">
                  {item.productId ? (
                    <Link
                      to="/org/products/$productId"
                      params={{ productId: item.productId }}
                      className="hover:underline"
                    >
                      {item.title}
                    </Link>
                  ) : (
                    item.title
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {format.number(item.units)}
                </TableCell>
                <TableCell className="pr-5 text-right tabular-nums">
                  {format.money(item.net)}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground h-20 text-center">
                {t('empty.noSales')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
